import { RegisterUserWizard } from "./RegisterUsersWizard.default";
import {
  getRegisterUsersTemplate,
  registerCompanyUser,
  registerCompanyUsersFile,
  registerForCompanyWithFilePreview,
} from "../repository/auth.repository";
import { retry } from "../utils/http";
import {
  getCompanies,
  getCompanyDepartments,
  getCompanyAssets,
  getCompanyUserRoles,
  getCountries,
  getLanguages,
} from "../repository/data.repository";
import { nameOf } from "../utils/typescript";
import {
  Company,
  CompanyAsset,
  CompanyRole,
  Country,
  Department,
  Language,
} from "../repository/data.default";
import { QueryOperation } from "../code/search-query/search-query";
import dayjs from "dayjs";
import DynamicFormWizardActions, {
  WizardStep,
} from "../controls/DynamicFormWizardActions";
import { phoneRule } from "../controls/PhoneInput";

export interface RegisterUsersWizardProps {
  readonly open?: boolean;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

function RegisterUsersWizard(props: RegisterUsersWizardProps) {
  // Define request functions for dynamic data
  const companiesRequest = (search: string) => {
    return retry(() =>
      getCompanies({
        searchQuery: {
          conditions: [
            {
              field: nameOf<Company>("name"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((dataItem) => ({
        display: dataItem.name,
        value: dataItem.uid,
        dataItem,
      })),
    );
  };

  const userRolesRequest = (search: string, formValues: any) => {
    if (!formValues?.company?.value) {
      return Promise.resolve([]);
    }

    return retry(() =>
      getCompanyUserRoles({
        searchQuery: {
          conditions: [
            {
              field: nameOf<CompanyRole>("name"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
            {
              field: nameOf<CompanyRole>("companyUid"),
              operation: QueryOperation.Equal,
              values: [formValues.company.value],
            },
            {
              field: nameOf<CompanyRole>("isManager"),
              operation: QueryOperation.Equal,
              values: [true],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    )
      .then(({ data }) => {
        return data.map((dataItem) => ({
          display: dataItem.name,
          value: dataItem.uid,
          dataItem,
        }));
      })
      .catch((error) => {
        return [];
      });
  };

  const departmentsRequest = (search: string, formValues: any) => {
    if (!formValues?.company?.value) {
      return Promise.resolve([]);
    }

    return retry(() =>
      getCompanyDepartments({
        searchQuery: {
          conditions: [
            {
              field: nameOf<Department>("name"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
            {
              field: nameOf<Department>("companyUid"),
              operation: QueryOperation.Equal,
              values: [formValues.company.value],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    )
      .then(({ data }) => {
        return data.map((dataItem) => ({
          display: dataItem.name,
          value: dataItem.uid,
          dataItem,
        }));
      })
      .catch((error) => {
        return [];
      });
  };

  // Optional asset (vessel/aircraft/site) the employee is currently mapped to — scoped to the chosen company.
  const assetsRequest = (search: string, formValues: any) => {
    if (!formValues?.company?.value) {
      return Promise.resolve([]);
    }

    return retry(() =>
      getCompanyAssets({
        searchQuery: {
          conditions: [
            {
              field: nameOf<CompanyAsset>("name"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
            {
              field: nameOf<CompanyAsset>("companyUid"),
              operation: QueryOperation.Equal,
              values: [formValues.company.value],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    )
      .then(({ data }) => {
        return data.map((dataItem) => ({
          display: dataItem.name,
          value: dataItem.uid,
          dataItem,
        }));
      })
      .catch(() => {
        return [];
      });
  };

  const testLanguagesRequest = (search: string) =>
    retry(() =>
      getLanguages({
        searchQuery: {
          conditions: [
            { field: nameOf<Language>("name"), operation: QueryOperation.StartsWith, values: [search] },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((d) => ({ display: d.name, value: d.uid, dataItem: d })),
    );

  const countriesRequest = (search: string) =>
    retry(() =>
      getCountries({
        searchQuery: {
          conditions: [
            { field: nameOf<Country>("name"), operation: QueryOperation.StartsWith, values: [search] },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((d) => ({ display: d.name, value: d.uid, dataItem: d })),
    );

  // Define wizard steps
  const steps: WizardStep[] = [
    {
      title: "Option",
      createSchema: (values) => ({
        groups: [
          {
            controls: [
              {
                type: "combobox",
                name: "company",
                label: "Company",
                rules: [
                  {
                    required: true,
                    message: "Company is required",
                  },
                ],
                request: companiesRequest,
              },
            ],
          },
        ],
      }),
    },
    {
      title: "User",
      createSchema: (values) => {
        return {
          groups: [
            {
              name: "usersInfos",
              label: "User",
              isList: true,
              controls: [
                {
                  type: "textbox",
                  name: "userName",
                  label: "User Name (optional)",
                },
                {
                  type: "textbox",
                  name: "firstName",
                  label: "First Name",
                  rules: [
                    {
                      required: true,
                      message: "First Name is required",
                    },
                  ],
                },
                {
                  type: "textbox",
                  name: "middleName",
                  label: "Middle Name (optional)",
                },
                {
                  type: "textbox",
                  name: "lastName",
                  label: "Last Name",
                  rules: [
                    {
                      required: true,
                      message: "Last Name is required",
                    },
                  ],
                },
                {
                  // H4: email is now required — without it the invite link cannot be sent and the account
                  // never activates. Previously accepted empty and silently created a dead account.
                  type: "textbox",
                  name: "email",
                  label: "Email",
                  rules: [
                    {
                      required: true,
                      message: "Email is required",
                    },
                    {
                      type: "email",
                      message: "Invalid email format",
                    },
                  ],
                },
                {
                  type: "phonebox",
                  name: "phoneNumber",
                  label: "Phone Number (optional)",
                  rules: [phoneRule],
                },
                {
                  type: "phonebox",
                  name: "companyPhoneNumber",
                  label: "Company Phone (optional)",
                  rules: [phoneRule],
                },
                {
                  type: "datepicker",
                  name: "dateOfBirth",
                  label: "Date Of Birth",
                  rules: [
                    {
                      required: true,
                      message: "Date Of Birth is required",
                    },
                    {
                      // Reject obviously-nonsense dates: future dates (not born yet) and anything before 1900.
                      validator: (_rule, value) => {
                        if (!value) return Promise.resolve();
                        const d = dayjs(value);
                        if (d.isAfter(dayjs().endOf("day")))
                          return Promise.reject(new Error("Date of Birth cannot be in the future"));
                        if (d.isBefore(dayjs("1900-01-01")))
                          return Promise.reject(new Error("Date of Birth cannot be before 1900"));
                        return Promise.resolve();
                      },
                    },
                  ],
                },
                {
                  type: "combobox",
                  name: "role",
                  label: "Role",
                  rules: [
                    {
                      required: true,
                      message: "Role is required",
                    },
                  ],
                  request: (search) => userRolesRequest(search, values),
                },
                {
                  type: "combobox",
                  name: "department",
                  label: "Department",
                  rules: [
                    {
                      required: true,
                      message: "Department is required",
                    },
                  ],
                  request: (search) => departmentsRequest(search, values),
                },
                {
                  type: "combobox",
                  name: "asset",
                  label: "Asset (optional)",
                  request: (search) => assetsRequest(search, values),
                },
                {
                  type: "combobox",
                  name: "testLanguage",
                  label: "Language (optional)",
                  request: testLanguagesRequest,
                },
                {
                  type: "combobox",
                  name: "country",
                  label: "Country (optional)",
                  request: countriesRequest,
                },
              ],
            },
          ],
        };
      },
    },
    {
      title: "Finish",
      content: (
        <div className="p-4">
          <p className="mb-2 font-medium">Ready to register.</p>
          <p className="m-0 text-gray-600">
            Click <b>Submit</b> to create the manager account(s) you entered. Use <b>Previous</b> to go back
            and review or change any field. Each new user will receive an activation email with instructions
            to set their password.
          </p>
        </div>
      ),
    },
  ];

  return (
    <DynamicFormWizardActions<RegisterUserWizard>
      open={props.open}
      title="Register Managers"
      steps={steps}
      onFinish={props.onFinish}
      onCancel={props.onCancel}
      showOptionSelection={true}
      optionSelectionText="Do you want to register users or import an excel file with users?"
      optionLabels={{
        single: "Register",
        multiple: "Import",
      }}
      fileUploadSupport={true}
      fileUploadText="Create or browse an Excel file with users"
      fileUploadButtonText="Browse Excel File"
      downloadTemplateSupport={true}
      downloadTemplateButtonText="Download Template"
      fileUploadSizeLimit={100}
      fileUploadErrorText="Excel size limit is 100mb"
      fileUploadAccept=".xls,.xlsx"
      initialValues={{
        company: undefined,
        usersInfos: [{} as any],
      }}
      apiCalls={{
        submit: async (values) => {
          const input = {
            companyUid: values.company?.value!,
            usersInfos: values.usersInfos.map(
              ({
                userName,
                email,
                firstName,
                middleName,
                lastName,
                dateOfBirth,
                role,
                department,
                asset,
                phoneNumber,
                companyPhoneNumber,
                testLanguage,
                country,
              }) => ({
                userName,
                email,
                firstName,
                middleName,
                lastName,
                dateOfBirth: dateOfBirth.format("YYYY-MM-DD"),
                roleUid: role.value,
                departmentUid: department.value,
                assetUid: asset?.value,
                phoneNumber,
                companyPhoneNumber,
                testLanguageUid: testLanguage?.value,
                countryUid: country?.value,
              }),
            ),
          };
          return registerCompanyUser(input);
        },
        submitFile: async (file, values) => {
          const input = {
            companyUid: values.company?.value!,
            file: file,
          };
          return registerCompanyUsersFile(input);
        },
        downloadTemplate: async () => {
          return getRegisterUsersTemplate();
        },
        previewFile: async (file, values) => {
          const { company } = values;
          const result = await registerForCompanyWithFilePreview({
            file,
            companyUid: company?.value!,
          });

          return {
            ...values,
            usersInfos: result.usersInfos.map(
              ({
                email,
                firstName,
                lastName,
                dateOfBirth,
                roleName,
                roleUid,
                departmentName,
                departmentUid,
                assetName,
                assetUid,
              }) => {
                return {
                  email,
                  firstName,
                  lastName,
                  dateOfBirth: dayjs(dateOfBirth, ["YYYY-MM-DD"]),
                  role: {
                    display: roleName,
                    value: roleUid,
                  },
                  department: {
                    display: departmentName,
                    value: departmentUid,
                  },
                  asset: assetUid
                    ? { display: assetName, value: assetUid }
                    : undefined,
                };
              },
            ),
          };
        },
      }}
      completeButtonText="Complete"
      retryButtonText="Retry"
      closeButtonText="Close"
      resetButtonText="Register Users"
      errorText="Something went wrong, try again"
    />
  );
}

export default RegisterUsersWizard;
